import React from 'react';
import PropTypes from 'prop-types';
import _, { findKey, findLastKey } from 'lodash';
import moment from 'moment';
import { Field } from 'redux-form';
import { TimeMap } from './data/time-data';
import { Helpers } from 'shared-components/helpers/helpers';
import AvailabilityInput from './availability-input';
import { TimeSelector } from './time-selector';
import Calendar from '../../../api/api-calendar';
import { FormattedDate, FormattedMessage, injectIntl } from 'react-intl';
import classNames from 'classnames';

class DateTimeInput extends React.Component {
  constructor (props) {
    super(props);

    // CaputuredEndTime & capturedStartTime:
    // The value of those states refers to the 'key' value from TimeMap, but also time_index from day availability API.
    // We use those keys to find the 'h' (hours), 'm', and 'overnight' values to create a start/ end date time hash.
    this.state = {
      completedInput: false,
      capturedEndTime: '',
      capturedStartTime: '',
      capturedDate: '',
      errorMsg: '',
      startTimes: [], // returned response from day availability api of start times
      endTimes: [], // returned response from day availability api of end times
      showDate: false,
      showStartTime: false,
      showEndTime: false
    };

    this.storeNewState = this.storeNewState.bind(this);
    this.isValid = this.isValid.bind(this);
    this.validateDateTime = this.validateDateTime.bind(this);
    this.actionFn = this.actionFn.bind(this);
    this.captureDate = this.captureDate.bind(this);
    this.captureTime = this.captureTime.bind(this);
    this.getStartEndHash = this.getStartEndHash.bind(this);
    this.getDayAvailability = this.getDayAvailability.bind(this);
    this.setTimeMaps = this.setTimeMaps.bind(this);
    this.toggleShowDate = this.toggleShowDate.bind(this);
    this.changeShowState = this.changeShowState.bind(this);
    this.checkBookingAvailability = this.checkBookingAvailability.bind(this);
  }

  componentWillMount () {
    // componentWillMount is phased out in the latest version of React (16.3) and changed to UNSAFE_componentWillMount().
    // The lifecycle will be completely depracated in the next major release.
    // 'Generally, we recommend using the constructor() instead for initializing state.'
    const { start, end } = this.props.dateTimeHash;
    if (start && end) {
      const startDate = start.date;
      const endDate = end.date;
      const dateString = `${startDate.m}-${startDate.d}-${startDate.y}`;
      const capturedStartTime = parseInt(this.findKeyFromHashVals(start.time), 10);
      const isOvernight = endDate.y > startDate.y || endDate.m > startDate.m ||  endDate.d > startDate.d;
      const capturedEndTime = parseInt(this.findKeyFromHashVals(end.time, isOvernight), 10);

      this.setState({
        capturedStartTime,
        capturedEndTime,
        capturedDate: moment(dateString, 'MM-DD-YYYY')
      });
    }
  }

  componentDidMount () {
    const { capturedStartTime, capturedEndTime, capturedDate } = this.state;
    // captureDate & capturedEndTime & capturedStartTime becomes available when there's a booking_schedule in the cookie
    // If all is available call validateDateTime which returns a newHash
    if ((capturedStartTime || 0 === capturedStartTime) && capturedEndTime && capturedDate) this.validateDateTime();

    // Render start times
    this.setTimeMaps();
  }

  componentWillUnmount () {
    this.actionFn();
  }

  checkBookingAvailability (isStartTimes) {
    const { startTimes, endTimes } = this.state;
    const { afterMidnight } = this.props;


    // find the time_index of a given start or end time
    const findTimeIndex = (availableTimes, time, afterMidnight) => {
      const timeObj = availableTimes.find(item => item.h === time.h
        && item.m === time.m
        && (afterMidnight || true !== item.overnight)
      );
      return timeObj ? timeObj.time_index : null;
    };

    const bookingStart = _.get(this.props, 'bookingStart', {});
    const bookingEnd = _.get(this.props, 'bookingEnd', {});

    if (!_.isEmpty(bookingStart) && !_.isEmpty(bookingEnd)) {
      const startIndex = isStartTimes
        ? findTimeIndex(startTimes, bookingStart.time, afterMidnight)
        : 0;

      const endIndex = findTimeIndex(
        endTimes,
        bookingEnd.time,
        afterMidnight || (bookingEnd.time.h === 0 && bookingEnd.time.m === 0)
      );

      const timeArray = isStartTimes ? startTimes : endTimes;

      timeArray.forEach((time) => {
        // startIndex will have a value for the invite 'start' time but not for the end time
        // since we already parse the timeArray for 'end' time down to 30min after start time in setTimeMaps function
        if (null !== startIndex && null !== endIndex) {
          // disabling any times that are not within the booking window
          const isInRange =
            time.time_index >= startIndex && time.time_index <= endIndex;
          time.available = isInRange;
        } else {
          time.available = false;
        }
      });

      return timeArray;
    }
    return isStartTimes ? startTimes : endTimes;
  }

  getStartEndHash (capturedDate, capturedStartTime, capturedEndTime) {
    let completeStartHash;
    let completeEndHash;

    if ((capturedStartTime || 0 === capturedStartTime) && capturedDate) {
      const startDateHash = Helpers.parseDate(capturedDate);
      completeStartHash = { start: {
        date: startDateHash,
        time: this.createHashFromKey(capturedStartTime)
      } };
    }

    if (capturedEndTime && capturedDate) {
      const isOvernight = TimeMap[capturedEndTime].overnight;
      const startDay = capturedDate.clone().startOf('day');
      const endDate = isOvernight ? startDay.add(1, 'd') : startDay;
      const endDateHash = Helpers.parseDate(endDate);
      completeEndHash = { end: {
        date: endDateHash,
        time: this.createHashFromKey(capturedEndTime)
      } };
    }

    return { completeStartHash, completeEndHash };
  }

  storeNewState (newState, cb) {
    this.setState(newState, () => {
      this.actionFn();
      if (cb) cb();
    });
  }

  createHashFromKey (key) {
    return {
      h: TimeMap[key].h,
      m: TimeMap[key].m
    };
  }

  findKeyFromHashVals (timeHash = {}, isOvernight = false) {
    const findFunc = isOvernight ? findLastKey : findKey;
    return findFunc(TimeMap, (time) => {
      return (time.h === timeHash.h && time.m === timeHash.m);
    });
  }

  captureTime (event, cb) {
    const { capturedDate } = this.state;
    const { isBookingForm, isReduxForm, onTimeChange, index } = this.props;
    // OnChange event of select
    const target = event.target;
    const value = target.getAttribute('data-value');
    const stateKey = target.getAttribute('data-key'); // this value refers to the time_index from day availability API
    const newState = {};

    if (isReduxForm) {
      onTimeChange(stateKey, value, index);
    }

    if (!capturedDate && isBookingForm) {
      newState.showDate = true;
    }

    if ('capturedStartTime' === stateKey) {
      newState.capturedStartTime = value;
      newState.showStartTime = false;
    } else if ('capturedEndTime' === stateKey) {
      newState.capturedEndTime = value;
      newState.showEndTime = false;
    }

    this.storeNewState(newState, () => {
      // call getDayAvailability to generate list of end times
      if ('capturedStartTime' === stateKey) this.getDayAvailability('end');
      cb();
    });
  }

  captureDate (date) {
    const { capturedStartTime, capturedEndTime } = this.state;
    const { isBookingForm, isReduxForm, onDateChange, index, intl } = this.props;
    const showStartTime = !capturedStartTime;
    const showEndTime = !capturedEndTime;
    // datepicker returns moment date and also takes as value to show
    const newState = {
      capturedDate: moment(date)
    };

    if (isReduxForm) {
      onDateChange(index, date, moment(date));
    }

    if (isBookingForm) {
      this.toggleShowDate();
      newState.showStartTime = showStartTime;
      newState.showEndTime = showEndTime;
    }

    this.storeNewState(newState, () => {
      // if we have a date then we generate a list of start times
      this.getDayAvailability('start', true);
      this.getDayAvailability('end');
    });
  }

  getDayAvailability (whichSelector, checkValidStartTime, callback) {
    // whichSelector: 'start' or 'end';
    // checkStartTime: bool to start check if start time is booked or\
    const { noAvailabilityGet, excludeBookingIds } = this.props;
    if (noAvailabilityGet) {
      return this.setTimeMaps(callback);
    }

    const { spaceId } = this.props;
    const { capturedDate, capturedStartTime } = this.state;

    // skip calling api to get end times if captureDate or capturedStartTime is not present
    if ('end' === whichSelector && (!capturedDate || !(capturedStartTime || 0 === capturedStartTime))) return null;

    let month;
    let year;
    let day;
    if (capturedDate) {
      month = capturedDate.get('month') + 1;
      year = capturedDate.get('year');
      day = capturedDate.get('date');
    }

    const excludeBookingsQuery = _.isArray(excludeBookingIds)
      ? _.chain(excludeBookingIds).map((bkID, i) => {
        const queryExtension = `exclude_booking_ids=${bkID}`;
        return 0 === i ? `&${queryExtension}` : queryExtension;
      }).join('&').value() : '';


    const startTimeIndex = capturedStartTime || 0 === capturedStartTime ? `&time_index=${capturedStartTime}` : '';

    const query = `bookings/${this.props.isHost ? 'host' : ''}/availability/space/${spaceId}/day/${whichSelector}?year=${year}&month=${month}&day=${day}${startTimeIndex}${excludeBookingsQuery}`;

    return Calendar.readAnonymous(query, (response) => {
      if (response.error || 400 < response.statusCode) {
        return this.setState({ errorMsg: <FormattedMessage id="elements_date_time.date_time_input.something_wrong" defaultMessage="Something went wrong. Please try again later." /> });
      }
      if ('start' === whichSelector) {
        const newState = {};
        const startTimes = _.get(response, ['start_availability'], []);
        newState.startTimes = startTimes;
        // Start of check for when user changes from a day that has an available start time, to a day where that start time is blocked.
        // Also checks on initial render (componentDidMount)
        if (checkValidStartTime) {
          // Check collection for matching time_index with capturedStartTime and the available flag to be false
          const isStartTimesUnavailable = _.some(startTimes, { time_index: parseInt(capturedStartTime, 10), available: false });
          if (isStartTimesUnavailable) {
            // Resetting capturedStartTime & capturedEndTime will reset the drodown to show the placeholder.
            newState.capturedStartTime = '';
            newState.capturedEndTime = '';
            // Resetting endTimes to an empty array will reset the end time drop down to show empty values.
            newState.endTimes = [];
          }
        }
        return this.setState({ ...newState }, () => {
          if (callback) callback();
        });
      }
      const endTimes = _.get(response, ['end_availability'], []);
      return this.setState({ endTimes }, () => {
        if (callback) callback();
      });
    });
  }

  isValid () {
    const { optional } = this.props;
    const validDateTime = this.validateDateTime(true);
    let errorFree = true;
    if (_.isEmpty(validDateTime) && !optional) {
      errorFree = false;
    }
    return errorFree;
  }

  validateDateTime (fromIsValid) {
    // fromIsValid: is a bool that is used to display a different error message if the function comes from isValid
    const { capturedDate, capturedStartTime, capturedEndTime, completedInput } = this.state;
    const startEndHash = this.getStartEndHash(capturedDate, capturedStartTime, capturedEndTime);
    const { completeStartHash, completeEndHash } = startEndHash;
    const newHash = _.merge({}, completeStartHash, completeEndHash);
    let errorMsg = '';
    if (completeStartHash && completeEndHash) {
      this.setState({
        completedInput: true,
        errorMsg: ''
      });
      return newHash;
    }
    if (!completeStartHash || !completeEndHash) {
      if (completedInput) {
        if (!capturedDate) { errorMsg = <FormattedMessage id="elements_date_time.date_time_input.enter_date" defaultMessage="Please enter date" />; }
        if (!capturedStartTime) { errorMsg = <FormattedMessage id="elements_date_time.date_time_input.enter_start_time" defaultMessage="Please enter start time" />; }
        if (!capturedEndTime) { errorMsg = <FormattedMessage id="elements_date_time.date_time_input.enter_end_time" defaultMessage="Please enter end time" />; }
      }
      if (fromIsValid) { errorMsg = <FormattedMessage id="elements_date_time.date_time_input.add_date_time" defaultMessage="Please add date and time." />; }
      this.setState({ errorMsg });
    }
    return {};
  }

  actionFn () {
    const { id, actionCb } = this.props;
    const action = this.props.actionFn;
    const firstNumber = id ? id.match(/\d/)[0] : null;
    const validDateTime = this.validateDateTime();

    if (!action || !validDateTime) { return; }
    action(firstNumber, validDateTime, (...args) => {
      actionCb(validDateTime, ...args);
    });
  }

  setTimeMaps (callback) {
    const { capturedStartTime } = this.state;
    const startTimes = _.filter(TimeMap, (value, key) => { return 35 >= key; });
    const endTimes = _.filter(TimeMap, (value, key) => { return key > parseInt(capturedStartTime, 10); });

    this.setState({
      startTimes,
      endTimes
    }, () => {
      if (callback) callback();
    });
  }

  toggleShowDate () {
    const { showDate } = this.state;

    this.setState({
      showDate: !showDate
    });
  }

  changeShowState (label, show) {
    if ('Start' === label) {
      this.setState({
        showStartTime: false
      });
    } else {
      this.setState({
        showEndTime: false
      });
    }
  }

  render () {
    const { endLabel, startLabel, dateLabel, preventEditDates, calendarPlacement, id, excludeBookingIds, isBookingForm, isReduxForm, field, index, country, groupContainerClassName = '', datePlaceholder, startTimePlaceholder, endTimePlaceholder, showDateLabel } = this.props;
    const errorMsgText = this.props.errorMsg || this.state.errorMsg;
    const { capturedDate, capturedEndTime, capturedStartTime, endTimes, startTimes, showDate, showStartTime, showEndTime } = this.state;
    const { spaceId } = this.props;
    const displayIsOvernight = capturedEndTime && (TimeMap[capturedEndTime] || {}).overnight;
    const availableStartTimes = this.checkBookingAvailability(true);
    const availableEndTimes = this.checkBookingAvailability(false);

    return (
      <div className={classNames({'col-xs-12': this.props.useBootstrapGrid})} data-testing-id='date-time-input'>
        <div className="date-time-form-feecalc">
          { isReduxForm
            ? <div className={groupContainerClassName}>
              <div className="date-group" tabIndex="0">
                { dateLabel ? <label htmlFor="lst-datepkr">{dateLabel} </label> : '' }
                <Field
                  name={`${field}.date`}
                  component={AvailabilityInput}
                  captureDate={this.captureDate}
                  selectedDay={capturedDate}
                  htmlId={`lst-datepkr-${index}`}
                  spaceId={spaceId}
                  calendarPlacement={calendarPlacement}
                  excludeBookingIds={excludeBookingIds}
                  toggleShowDate={this.toggleShowDate}
                  showDate={showDate}
                  isBookingForm
                  isReduxForm
                  isHost={this.props.isHost}
                  placeholder={datePlaceholder}
                  country={country}
                />
              </div>

              <div className="time-group">
                <Field
                  name={`${field}.startTime`}
                  component={TimeSelector}
                  placeholder={ startTimePlaceholder || <FormattedMessage id="elements_date_time.date_time_input.start_time" defaultMessage="start time" /> }
                  dropDownToRender="capturedStartTime"
                  availability={startTimes}
                  capturedDate={capturedDate}
                  selectValue={capturedStartTime}
                  onChangeHandler={this.captureTime}
                  availabilityFn={this.getDayAvailability}
                  showStartTime={showStartTime}
                  changeShowState={this.changeShowState}
                  htmlId={index}
                  isReduxForm
                />
                <Field
                  name={`${field}.endTime`}
                  component={TimeSelector}
                  htmlId={id}
                  label={endLabel}
                  placeholder={ endTimePlaceholder || <FormattedMessage id="elements_date_time.date_time_input.end_time" defaultMessage="end time" /> }
                  dropDownToRender="capturedEndTime"
                  selectDisabled={0 === endTimes.length}
                  capturedDate={capturedDate}
                  availability={endTimes}
                  selectValue={capturedEndTime}
                  onChangeHandler={this.captureTime}
                  availabilityFn={this.getDayAvailability}
                  showEndTime={showEndTime}
                  changeShowState={this.changeShowState}
                  htmlId={index}
                  isReduxForm
                />
              </div>
            </div>
            : <div>
              { preventEditDates
                ?
                <>
                  {showDateLabel &&
                    <div className="heavy margin-md-right margin-md-bottom">
                      {capturedDate
                        && <FormattedDate
                          value={capturedDate}
                          year="numeric"
                          month="short"
                          day="numeric"
                          weekday="short"
                        />}
                    </div>
                  }
                </>
                : <div className="date-group" tabIndex="0">
                  { dateLabel ? <label htmlFor="lst-datepkr">{dateLabel} </label> : '' }
                  <AvailabilityInput
                    captureDate={this.captureDate}
                    selectedDay={capturedDate}
                    htmlId={`lst-datepkr-${index}`}
                    spaceId={spaceId}
                    calendarPlacement={calendarPlacement}
                    excludeBookingIds={excludeBookingIds}
                    toggleShowDate={this.toggleShowDate}
                    showDate={showDate}
                    isBookingForm={isBookingForm}
                    isHost={this.props.isHost}
                    placeholder={datePlaceholder}
                    country={country}
                  />
                </div>
              }

              <div className="time-group">
                <TimeSelector
                  htmlId={id}
                  label={startLabel}
                  placeholder={ startTimePlaceholder || <FormattedMessage id="elements_date_time.date_time_input.captured_start_time" defaultMessage="start time" /> }
                  dropDownToRender="capturedStartTime"
                  availability={availableStartTimes}
                  capturedDate={capturedDate}
                  selectValue={capturedStartTime}
                  onChangeHandler={this.captureTime}
                  availabilityFn={this.getDayAvailability}
                  showStartTime={showStartTime}
                  changeShowState={this.changeShowState}
                />
                <TimeSelector
                  htmlId={id}
                  label={endLabel}
                  placeholder={ endTimePlaceholder || <FormattedMessage id="elements_date_time.date_time_input.captured_end_time" defaultMessage="end time" /> }
                  dropDownToRender="capturedEndTime"
                  selectDisabled={0 === endTimes.length}
                  capturedDate={capturedDate}
                  availability={availableEndTimes}
                  selectValue={capturedEndTime}
                  onChangeHandler={this.captureTime}
                  availabilityFn={this.getDayAvailability}
                  showEndTime={showEndTime}
                  changeShowState={this.changeShowState}
                />
              </div>
            </div>
          }
        </div>
        { !!errorMsgText && <div className="help-block inline-help text-danger text-left">{errorMsgText}</div> }
        { !!displayIsOvernight && <div className="text-left text-small text-gray-600">
          <FormattedMessage id="elements_date_time.date_time_input.ends_next" defaultMessage="Booking ends the next day." />
        </div>
        }
      </div>
    );
  }
}

DateTimeInput.defaultProps = {
  noAvailabilityGet: false,
  useBootstrapGrid: true
};

DateTimeInput.propTypes = {
  id: PropTypes.string,
  actionFn: PropTypes.func,
  actionCb: PropTypes.func,
  errorMsg: PropTypes.string,
  dateLabel: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.object
  ]),
  preventEditDates: PropTypes.bool,
  startLabel: PropTypes.string,
  endLabel: PropTypes.string,
  dateTimeHash: PropTypes.object,
  optional: PropTypes.bool,
  spaceId: PropTypes.string,
  noAvailabilityGet: PropTypes.bool,
  calendarPlacement: PropTypes.string,
  excludeBookingIds: PropTypes.array,
  isBookingForm: PropTypes.bool,
  isHost: PropTypes.bool,
  isReduxForm: PropTypes.bool,
  country: PropTypes.string,
  groupContainerClassName: PropTypes.string,
  startTimePlaceholder: PropTypes.string,
  endTimePlaceholder: PropTypes.string,
  datePlaceholder: PropTypes.string,
  useBootstrapGrid: PropTypes.bool
};

exports.DateTimeInput = injectIntl(DateTimeInput, { withRef: true });
